
import { useState } from 'react';

export default function PartnershipApplication() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    position: '',
    investmentCapacity: '',
    partnershipTier: '',
    investmentExperience: '',
    businessBackground: '',
    partnershipGoals: '',
    timeframe: '',
    referralSource: '',
    additionalInfo: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d439tlheds0ugd973l90', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          company: '',
          position: '',
          investmentCapacity: '',
          partnershipTier: '',
          investmentExperience: '',
          businessBackground: '',
          partnershipGoals: '',
          timeframe: '',
          referralSource: '',
          additionalInfo: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="partnership-application" className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Partnership Application</h2>
          <p className="text-xl text-gray-600">
            Take the first step towards becoming an OED partner and owner
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8">
          {submitStatus === 'success' && (
            <div className="mb-8 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <i className="ri-check-circle-line text-green-500 text-xl mr-3"></i>
                <div>
                  <h3 className="text-green-800 font-semibold">Application Submitted Successfully!</h3>
                  <p className="text-green-700 text-sm">Our partnership team will review your application and contact you within 48 hours.</p>
                </div>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <i className="ri-error-warning-line text-red-500 text-xl mr-3"></i>
                <div>
                  <h3 className="text-red-800 font-semibold">Submission Error</h3>
                  <p className="text-red-700 text-sm">There was an error submitting your application. Please try again.</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} data-readdy-form>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  required
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  required
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                  Company/Organization
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                />
              </div>
              <div>
                <label htmlFor="position" className="block text-sm font-medium text-gray-700 mb-2">
                  Position/Title
                </label>
                <input
                  type="text"
                  id="position"
                  name="position"
                  value={formData.position}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="investmentCapacity" className="block text-sm font-medium text-gray-700 mb-2">
                  Investment Capacity *
                </label>
                <select
                  id="investmentCapacity"
                  name="investmentCapacity"
                  required
                  value={formData.investmentCapacity}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8"
                >
                  <option value="">Select investment range</option>
                  <option value="$250,000 - $500,000">$250,000 - $500,000</option>
                  <option value="$500,000 - $1,000,000">$500,000 - $1,000,000</option>
                  <option value="$1,000,000 - $2,500,000">$1,000,000 - $2,500,000</option>
                  <option value="$2,500,000+">$2,500,000+</option>
                </select>
              </div>
              <div>
                <label htmlFor="partnershipTier" className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Partnership Tier *
                </label>
                <select
                  id="partnershipTier"
                  name="partnershipTier"
                  required
                  value={formData.partnershipTier}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8"
                >
                  <option value="">Select partnership tier</option>
                  <option value="Strategic Partner">Strategic Partner</option>
                  <option value="Executive Partner">Executive Partner</option>
                  <option value="Founding Partner">Founding Partner</option>
                  <option value="Custom Partnership">Custom Partnership</option>
                </select>
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="investmentExperience" className="block text-sm font-medium text-gray-700 mb-2">
                Investment Experience *
              </label>
              <select
                id="investmentExperience"
                name="investmentExperience"
                required
                value={formData.investmentExperience}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8"
              >
                <option value="">Select experience level</option>
                <option value="First-time investor">First-time investor</option>
                <option value="Some experience (1-5 years)">Some experience (1-5 years)</option>
                <option value="Experienced investor (5-15 years)">Experienced investor (5-15 years)</option>
                <option value="Seasoned investor (15+ years)">Seasoned investor (15+ years)</option>
                <option value="Professional investor/Fund manager">Professional investor/Fund manager</option>
              </select>
            </div>

            <div className="mb-6">
              <label htmlFor="businessBackground" className="block text-sm font-medium text-gray-700 mb-2">
                Business Background *
              </label>
              <textarea
                id="businessBackground"
                name="businessBackground"
                required
                rows={4}
                maxLength={500}
                value={formData.businessBackground}
                onChange={handleInputChange}
                placeholder="Describe your professional background, industry experience, and relevant qualifications..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm resize-none"
              />
              <div className="text-right text-sm text-gray-500 mt-1">
                {formData.businessBackground.length}/500 characters
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="partnershipGoals" className="block text-sm font-medium text-gray-700 mb-2">
                Partnership Goals *
              </label>
              <textarea
                id="partnershipGoals"
                name="partnershipGoals"
                required
                rows={4}
                maxLength={500}
                value={formData.partnershipGoals}
                onChange={handleInputChange}
                placeholder="What are your goals for this partnership? What value do you hope to bring to OED?"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm resize-none"
              />
              <div className="text-right text-sm text-gray-500 mt-1">
                {formData.partnershipGoals.length}/500 characters
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="timeframe" className="block text-sm font-medium text-gray-700 mb-2">
                  Investment Timeframe *
                </label>
                <select
                  id="timeframe"
                  name="timeframe"
                  required
                  value={formData.timeframe}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8"
                >
                  <option value="">Select timeframe</option>
                  <option value="Immediately">Immediately</option>
                  <option value="Within 30 days">Within 30 days</option>
                  <option value="Within 60 days">Within 60 days</option>
                  <option value="Within 90 days">Within 90 days</option>
                  <option value="6 months or longer">6 months or longer</option>
                </select>
              </div>
              <div>
                <label htmlFor="referralSource" className="block text-sm font-medium text-gray-700 mb-2">
                  How did you hear about us?
                </label>
                <select
                  id="referralSource"
                  name="referralSource"
                  value={formData.referralSource}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8"
                >
                  <option value="">Select source</option>
                  <option value="Google search">Google search</option>
                  <option value="Social media">Social media</option>
                  <option value="Referral from existing partner">Referral from existing partner</option>
                  <option value="Industry publication">Industry publication</option>
                  <option value="Investment network">Investment network</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div className="mb-8">
              <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700 mb-2">
                Additional Information
              </label>
              <textarea
                id="additionalInfo"
                name="additionalInfo"
                rows={4}
                maxLength={500}
                value={formData.additionalInfo}
                onChange={handleInputChange}
                placeholder="Any additional information you'd like to share about your interest in partnering with OED..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm resize-none"
              />
              <div className="text-right text-sm text-gray-500 mt-1">
                {formData.additionalInfo.length}/500 characters
              </div>
            </div>

            <div className="text-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-emerald-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Submitting Application...
                  </>
                ) : (
                  'Submit Partnership Application'
                )}
              </button>
            </div>

            <div className="mt-6 text-center text-sm text-gray-600">
              <p>
                By submitting this application, I acknowledge that I have the financial capacity for the indicated investment level and agree to OED's partnership evaluation process. All information provided is accurate and complete.
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
